import { PILLARS, SITE_ORIGINS, siteImagePath, type BlogPayload, type Channel, type InstagramPayload, type LinkedInCard, type LinkedInPayload, type YouTubePayload } from "@stratcms/sdk";
import { h, icon } from "./dom";

export interface Editor {
  el: HTMLElement;
  read(): unknown;
  onChange(fn: () => void): void;
}

const SERVICE_NAMES: Record<string, string> = {
  "business-consulting": "Business Consulting",
  "compliance-accreditation": "Compliance and Accreditation",
  "infrastructure-environment": "Infrastructure, Built Environment and Logistics",
  "ai-digitisation": "AI, Digitisation and Automation",
  cybersecurity: "Cybersecurity",
  "coaching-change": "Coaching and Change Management",
};

function field(label: string, input: HTMLElement, hint?: string | HTMLElement) {
  return h("label", { class: "field" }, h("span", { class: "field-label" }, label), input, hint ? h("span", { class: "field-hint" }, hint) : null);
}

function counter(el: HTMLInputElement | HTMLTextAreaElement, max: number, fold?: number) {
  const out = h("span", { class: "count-hint" });
  const update = () => {
    const n = el.value.length;
    out.textContent = `${n} / ${max}${fold ? (n > fold ? ` · folds after ${fold} (LinkedIn shows "...more")` : ` · all visible before the fold`) : ""}`;
    out.classList.toggle("over", n > max);
  };
  el.addEventListener("input", update);
  update();
  return out;
}

function watch(root: HTMLElement): (fn: () => void) => void {
  return (fn) => {
    root.addEventListener("input", fn);
    root.addEventListener("change", fn);
  };
}

export function blogEditor(p: BlogPayload, readOnly: boolean): Editor {
  const title = h("input", { type: "text", value: p.title, maxlength: 200, required: true, disabled: readOnly, "data-field": "title" });
  const summary = h("textarea", { rows: 3, maxlength: 400, disabled: readOnly, "data-field": "summary" });
  summary.value = p.summary;
  const service = h("select", { class: "select", disabled: readOnly, "data-field": "service" }, ...PILLARS.map((s) => h("option", { value: s, selected: p.service === s }, SERVICE_NAMES[s] ?? s)));
  const date = h("input", { type: "date", value: p.date, disabled: readOnly, "data-field": "date" });
  const body = h("textarea", { rows: 22, class: "code", spellcheck: "true", disabled: readOnly, "data-field": "body" });
  body.value = p.body;
  // Media without R2: a path to an image the website already serves. The server checks it exists on the live site.
  const hero = h("input", { type: "text", value: p.heroImage ?? "", maxlength: 300, placeholder: "/img/cyber-shield.webp", disabled: readOnly, "data-field": "heroImage", autocomplete: "off", spellcheck: "false" });
  const heroAlt = h("input", { type: "text", value: p.heroAlt ?? "", maxlength: 300, placeholder: "What the image shows", disabled: readOnly, "data-field": "heroAlt" });
  const thumb = h("img", { class: "hero-thumb", alt: "", hidden: true, "data-testid": "hero-thumb" }) as HTMLImageElement;
  const heroNote = h("span", { class: "field-hint", "data-testid": "hero-note" });
  const showHero = () => {
    const v = hero.value.trim();
    const path = v ? siteImagePath(v) : null;
    thumb.hidden = !path;
    if (path) thumb.src = `${SITE_ORIGINS[0]}${path}`;
    heroNote.textContent = !v
      ? "Optional. Uploads are off (no R2): use an image already on strategyedge.co.za, from its /img folder."
      : path
        ? "Checked against the live website when you save."
        : "Not allowed: only /img/... images on strategyedge.co.za (webp, png, jpg, gif, avif).";
    heroNote.classList.toggle("over", !!v && !path);
  };
  hero.addEventListener("input", showHero);
  showHero();
  const el = h(
    "div",
    { class: "editor" },
    field("Title", title, counter(title, 200)),
    field("Summary", summary, counter(summary, 400)),
    h("div", { class: "row2" }, field("Related service", service), field("Date shown", date)),
    h("div", { class: "row2" }, field("Hero image (website path)", hero, heroNote), field("Hero image description", heroAlt)),
    thumb,
    field("Article body (HTML)", body, "Allowed: p, h2, h3, h4, lists, links, strong, em, blockquote, tables, and images from the website's /img folder. Anything else is removed when you save."),
  );
  return {
    el,
    read: () => ({
      title: title.value.trim(),
      summary: summary.value.trim(),
      service: service.value,
      date: date.value,
      body: body.value,
      ...(p.heroAssetId ? { heroAssetId: p.heroAssetId } : {}),
      ...(hero.value.trim() ? { heroImage: hero.value.trim() } : {}),
      ...(heroAlt.value.trim() ? { heroAlt: heroAlt.value.trim() } : {}),
    }),
    onChange: watch(el),
  };
}

const LAYOUTS = ["cover", "statement", "quote", "stat", "list", "steps", "cta"];

function cardEditor(c: LinkedInCard, i: number, readOnly: boolean, ctl: { remove(): void; move(d: number): void }) {
  const layout = h("select", { class: "select", disabled: readOnly, "data-k": "layout" }, ...LAYOUTS.map((l) => h("option", { value: l, selected: c.layout === l }, l)));
  const eyebrow = h("input", { type: "text", value: (c.eyebrow as string) ?? "", disabled: readOnly, "data-k": "eyebrow" });
  const title = h("input", { type: "text", value: (c.title as string) ?? "", disabled: readOnly, "data-k": "title" });
  const body = h("textarea", { rows: 2, disabled: readOnly, "data-k": "body" });
  body.value = (c.body as string) ?? "";
  const stat = h("input", { type: "text", value: (c.stat as string) ?? "", disabled: readOnly, "data-k": "stat" });
  const items = h("textarea", { rows: 3, disabled: readOnly, "data-k": "items" });
  items.value = ((c.items as string[]) ?? []).map(String).join("\n");
  const source = h("input", { type: "text", value: (c.source as string) ?? "", disabled: readOnly, "data-k": "source" });
  const el = h(
    "fieldset",
    { class: "card-edit" },
    h(
      "legend",
      {},
      `Card ${i + 1}`,
      readOnly
        ? null
        : h(
            "span",
            { class: "card-tools" },
            h("button", { type: "button", class: "icon-btn", "aria-label": "Move up", onclick: () => ctl.move(-1) }, icon("up")),
            h("button", { type: "button", class: "icon-btn", "aria-label": "Move down", onclick: () => ctl.move(1) }, icon("down")),
            h("button", { type: "button", class: "icon-btn", "aria-label": "Remove card", onclick: () => ctl.remove() }, icon("trash")),
          ),
    ),
    h("div", { class: "row2" }, field("Layout", layout), field("Eyebrow", eyebrow)),
    field("Title", title, "Wrap a word in **double stars** for the teal highlight."),
    field("Body", body),
    h("div", { class: "row2" }, field("Stat (stat layout)", stat), field("Source", source)),
    field("Items, one per line", items, 'For list and steps. "Heading::detail" makes a bold heading.'),
  );
  const read = (): LinkedInCard => {
    const out: Record<string, unknown> = { ...c, layout: layout.value };
    const set = (k: string, v: string) => {
      if (v.trim()) out[k] = v.trim();
      else delete out[k];
    };
    set("eyebrow", eyebrow.value);
    set("title", title.value);
    set("body", body.value);
    set("stat", stat.value);
    set("source", source.value);
    const list = items.value.split("\n").map((x) => x.trim()).filter(Boolean);
    if (list.length) out.items = list;
    else delete out.items;
    return out as LinkedInCard;
  };
  return { el, read };
}

export function linkedinEditor(p: LinkedInPayload, readOnly: boolean): Editor {
  const text = h("textarea", { rows: 14, maxlength: 3000, disabled: readOnly, "data-field": "text" });
  text.value = p.text;
  const docTitle = h("input", { type: "text", value: p.documentTitle ?? "", maxlength: 200, disabled: readOnly, "data-field": "documentTitle" });
  let cards = [...p.cards];
  const list = h("div", { class: "cards" });
  let editors: { el: HTMLElement; read(): LinkedInCard }[] = [];
  const draw = () => {
    list.replaceChildren();
    editors = cards.map((c, i) =>
      cardEditor(c, i, readOnly, {
        remove: () => {
          cards = editors.map((e) => e.read()).filter((_, k) => k !== i);
          draw();
          list.dispatchEvent(new Event("change", { bubbles: true }));
        },
        move: (d) => {
          const cur = editors.map((e) => e.read());
          const j = i + d;
          if (j < 0 || j >= cur.length) return;
          [cur[i], cur[j]] = [cur[j]!, cur[i]!];
          cards = cur;
          draw();
          list.dispatchEvent(new Event("change", { bubbles: true }));
        },
      }),
    );
    for (const e of editors) list.appendChild(e.el);
  };
  draw();
  const add = readOnly
    ? null
    : h(
        "button",
        {
          type: "button",
          class: "btn btn-ghost",
          onclick: () => {
            cards = [...editors.map((e) => e.read()), { layout: "statement", title: "" }];
            draw();
          },
        },
        icon("plus"),
        h("span", {}, "Add card"),
      );
  const el = h(
    "div",
    { class: "editor" },
    field("Post text", text, counter(text, 3000, 210)),
    field("Document title (shown above a multi-card carousel)", docTitle),
    h("h3", { class: "sub" }, `Cards (${cards.length ? "rendered at 1080x1350" : "none: a text-only post"})`),
    list,
    add,
  );
  return {
    el,
    read: () => ({ text: text.value, cards: editors.map((e) => e.read()), ...(docTitle.value.trim() ? { documentTitle: docTitle.value.trim() } : {}), assetIds: p.assetIds ?? [] }),
    onChange: watch(el),
  };
}

export function instagramEditor(p: InstagramPayload, readOnly: boolean): Editor {
  const caption = h("textarea", { rows: 10, maxlength: 2200, disabled: readOnly });
  caption.value = p.caption;
  const el = h("div", { class: "editor" }, field("Caption", caption, counter(caption, 2200)), h("p", { class: "fine" }, `${p.assetIds.length} image(s) attached. New images cannot be added: uploads are off (no R2).`));
  return { el, read: () => ({ ...p, caption: caption.value }), onChange: watch(el) };
}

export function youtubeEditor(p: YouTubePayload, readOnly: boolean): Editor {
  const title = h("input", { type: "text", value: p.title, maxlength: 100, disabled: readOnly });
  const desc = h("textarea", { rows: 10, maxlength: 5000, disabled: readOnly });
  desc.value = p.description;
  const tags = h("input", { type: "text", value: p.tags.join(", "), disabled: readOnly });
  const privacy = h("select", { class: "select", disabled: readOnly }, ...["public", "unlisted", "private"].map((x) => h("option", { value: x, selected: p.privacy === x }, x)));
  const el = h("div", { class: "editor" }, field("Title", title, counter(title, 100)), field("Description", desc), h("div", { class: "row2" }, field("Tags (comma separated)", tags), field("Privacy", privacy)));
  return {
    el,
    read: () => ({ ...p, title: title.value, description: desc.value, tags: tags.value.split(",").map((t) => t.trim()).filter(Boolean), privacy: privacy.value }),
    onChange: watch(el),
  };
}

export function editorFor(channel: Channel, payload: unknown, readOnly: boolean): Editor {
  switch (channel) {
    case "blog":
      return blogEditor(payload as BlogPayload, readOnly);
    case "linkedin":
      return linkedinEditor(payload as LinkedInPayload, readOnly);
    case "instagram":
      return instagramEditor(payload as InstagramPayload, readOnly);
    case "youtube":
      return youtubeEditor(payload as YouTubePayload, readOnly);
  }
}

export function templateFor(channel: Channel): unknown {
  const today = new Date(Date.now() + 2 * 3600_000).toISOString().slice(0, 10);
  switch (channel) {
    case "blog":
      return { title: "", summary: "", service: "business-consulting", date: today, body: '<p class="lead"></p>\n<h2></h2>\n<p></p>' };
    case "linkedin":
      return { text: "", cards: [{ layout: "cover", eyebrow: "", title: "" }], assetIds: [] };
    case "instagram":
      return { caption: "", assetIds: [], altText: [] };
    case "youtube":
      return { title: "", description: "", tags: [], privacy: "public" };
  }
}
